import { useEffect, useState, useCallback } from "react"
import { useParams, Link, useLocation } from "react-router-dom"
import { SEO } from '@alascorporation/seo'
import { siteConfig } from '../config/seo.config'
import { Lock, ArrowLeft, ArrowRight, CheckCircle, Loader2 } from "lucide-react"
import { redirectToSso } from "@alascorporation/api-client"
import api from "../lib/api"
import { useAuthStore } from "../stores/authStore"
import { trackEvent } from "../lib/meta-pixel"
import { Badge } from "@/components/ui/badge"
import { PageHeader, PageSpinner } from "@/components/page"
import { getProgramLabel } from "../config/schools"
import { ssoConfig } from "../lib/sso"
import { useWompiCheckout } from "../hooks/useWompiCheckout"

interface Course {
  id: string
  title: string
  slug: string
  shortDescription?: string | null
  difficultyLevel: string
  estimatedHours?: number | null
  _count?: { lessonCourses: number }
}

interface CourseProgram {
  orderIndex: number
  course: Course
}

interface ProgramLevel {
  id: string
  name: string
  levelNumber: number
  description?: string | null
  price: number
  currency: string
  isPublished: boolean
  courses: CourseProgram[]
}

interface Program {
  id: string
  name: string
  slug: string
  description?: string | null
  shortDescription?: string | null
  color?: string | null
  icon?: string | null
  levels: ProgramLevel[]
}

export default function ProgramDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const { isAuthenticated } = useAuthStore()

  const [program, setProgram] = useState<Program | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null)
  const [enrolledLevelIds, setEnrolledLevelIds] = useState<Set<string>>(new Set())
  const [buyingLevelId, setBuyingLevelId] = useState<string | null>(null)
  const [buyError, setBuyError] = useState<string | null>(null)

  const loadProgram = useCallback(async () => {
    setLoadErrorMessage(null)

    let p: Program
    try {
      const { data } = await api.get(`/academy/programs/${slug}`)
      p = data.data
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      setLoadErrorMessage(
        status === 404
          ? 'Este programa no existe o fue movido.'
          : 'No pudimos cargar este programa. Verifica tu conexión e intenta de nuevo.',
      )
      setLoading(false)
      return
    }

    try {
      setProgram(p)
      trackEvent('ViewContent', {
        content_name: p.name,
        content_ids: [p.id],
        content_type: 'product',
      })

      if (isAuthenticated) {
        try {
          const enrolled = await api.get('/academy/courses/enrolled/me')
          const enrolledCourses: { id: string; access_via_program_level?: boolean }[] = enrolled.data.data
          // Determine which levels have at least one enrolled course via program level
          const enrolledCourseIds = new Set(
            enrolledCourses.filter(c => c.access_via_program_level).map(c => c.id)
          )
          const accessedLevelIds = new Set<string>()
          for (const level of p.levels) {
            const hasAccess = level.courses.some(cp => enrolledCourseIds.has(cp.course.id))
            if (hasAccess) accessedLevelIds.add(level.id)
          }
          setEnrolledLevelIds(accessedLevelIds)
        } catch {
          // Optional enrollment lookup can fail without blocking program view.
        }
      }
    } catch (err) {
      console.error('Unexpected error rendering program:', err)
      setLoadErrorMessage('No pudimos cargar este programa. Verifica tu conexión e intenta de nuevo.')
    } finally {
      setLoading(false)
    }
  }, [slug, isAuthenticated])

  useEffect(() => { loadProgram() }, [loadProgram])

  const { startCheckout } = useWompiCheckout(() => setBuyingLevelId(null))

  const handleBuyLevel = async (level: ProgramLevel) => {
    if (!isAuthenticated) {
      redirectToSso(ssoConfig, location.pathname)
      return
    }
    setBuyError(null)
    setBuyingLevelId(level.id)
    trackEvent('InitiateCheckout', {
      content_name: level.name,
      content_ids: [level.id],
      currency: level.currency,
      value: Number(level.price),
    })
    const error = await startCheckout('/academy/payments/create', { type: 'program_level', program_level_id: level.id })
    if (error) {
      setBuyError(error)
      setBuyingLevelId(null)
    }
  }

  if (loading) {
    return (
      <PageSpinner label="Cargando programa" />
    )
  }

  if (loadErrorMessage || !program) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background text-center">
        <p className="text-lg font-bold text-foreground">{loadErrorMessage || 'No pudimos cargar este programa.'}</p>
        <div className="flex gap-3">
          <button
            onClick={() => loadProgram()}
            className="rounded-lg border border-border bg-card px-6 py-2.5 text-sm font-bold text-foreground motion-press transition-[background-color,transform] hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            Reintentar
          </button>
          <Link
            to="/courses"
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground motion-press transition-[filter,transform] hover:brightness-95 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            Ver catálogo
          </Link>
        </div>
      </div>
    )
  }

  const totalCourses = program.levels.reduce((acc, l) => acc + l.courses.length, 0)
  const totalHours = program.levels.reduce((acc, l) =>
    acc + l.courses.reduce((s, cp) => s + (cp.course.estimatedHours ?? 0), 0), 0)

  return (
    <div className="min-h-screen text-foreground">
      {program && (
        <SEO
          config={siteConfig}
          title={`${program.name} | Programa Aeroespacial`}
          description={program.description?.slice(0, 160) || siteConfig.defaultDescription}
          path={`/programs/${program.slug}`}
          keywords={['programa aeroespacial', program.name, 'STEM Colombia']}
        />
      )}
      {/* Hero */}
      <section className="relative overflow-hidden pt-24 pb-16">
        <div className="container relative mx-auto max-w-5xl px-6">
          <Link to="/courses" className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Volver a cursos
          </Link>
          <div className="mt-4">
            <PageHeader title={program.name} />
          </div>
          {program.description && (
            <p className="mt-4 max-w-2xl text-lg text-muted-foreground leading-relaxed">
              {program.description}
            </p>
          )}
          <div className="mt-8 flex flex-wrap gap-4">
            {[
              { label: 'Niveles', value: program.levels.length },
              { label: 'Cursos', value: totalCourses },
              { label: 'Horas', value: `${totalHours}h` },
            ].map(stat => (
              <div key={stat.label} className="rounded-lg border border-border bg-card px-6 py-4">
                <p className="text-2xl font-extrabold text-foreground">{stat.value}</p>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Levels */}
      <section className="pb-24">
        <div className="container mx-auto max-w-5xl px-6">
          <h2 className="mb-8 text-2xl font-bold text-foreground">Niveles del programa</h2>

          {buyError && (
            <div className="mb-6 rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {buyError}
            </div>
          )}

          <div className="space-y-4">
            {program.levels.map((level, idx) => {
              const isEnrolled = enrolledLevelIds.has(level.id)
              const prevLevel = idx > 0 ? program.levels[idx - 1] : null
              const isLocked = !isEnrolled && prevLevel !== null && !enrolledLevelIds.has(prevLevel.id)
              const isBuying = buyingLevelId === level.id

              return (
                <div
                  key={level.id}
                  className={`rounded-xl border p-6 transition-colors ${
                    isEnrolled
                      ? 'border-primary/30 bg-primary/[0.04]'
                      : isLocked
                      ? 'border-border bg-muted/30 opacity-60'
                      : 'border-border bg-card hover:bg-accent/40'
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <span className="text-micro font-bold uppercase tracking-wider text-muted-foreground">
                          NIVEL {level.levelNumber}
                        </span>
                        {isEnrolled && (
                          <Badge className="border-border bg-muted text-foreground">
                            <CheckCircle className="mr-1 h-3 w-3" aria-hidden="true" /> Acceso activo
                          </Badge>
                        )}
                        {isLocked && (
                          <Badge variant="outline">
                            <Lock className="mr-1 h-3 w-3" aria-hidden="true" /> Requiere nivel anterior
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-xl font-bold text-foreground mb-1">{level.name}</h3>
                      {level.description && (
                        <p className="text-sm text-muted-foreground mb-3">{level.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground">{level.courses.length} curso{level.courses.length !== 1 ? 's' : ''}</p>
                    </div>

                    <div className="flex flex-col items-end gap-3 shrink-0">
                      {isEnrolled ? (
                        <Link
                          to={`/courses/${level.courses[0]?.course.slug ?? ''}`}
                          className="flex items-center gap-2 rounded-lg border border-border px-5 py-2.5 text-sm font-bold text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
                        >
                          Ir al nivel <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </Link>
                      ) : (
                        <>
                          <p className="text-2xl font-extrabold text-foreground">
                            ${Number(level.price).toLocaleString('es-CO')}{' '}
                            <span className="text-sm font-normal text-muted-foreground">{level.currency}</span>
                          </p>
                          <button
                            onClick={() => !isLocked && handleBuyLevel(level)}
                            disabled={isLocked || isBuying}
                            className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground motion-press transition-[filter,transform] hover:brightness-95 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"
                          >
                            {isBuying ? (
                              <><Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> Procesando...</>
                            ) : isLocked ? (
                              <><Lock className="h-4 w-4" aria-hidden="true" /> Bloqueado</>
                            ) : (
                              <>Comprar nivel</>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Course list */}
                  {level.courses.length > 0 && (
                    <div className="mt-4 grid gap-2 sm:grid-cols-2">
                      {level.courses.map(cp => (
                        <div key={cp.course.id} className="flex items-center gap-3 rounded-xl border border-border/60 bg-background px-4 py-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                            <span className="text-xs font-black text-muted-foreground">{String(cp.orderIndex + 1).padStart(2, '0')}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">{cp.course.title}</p>
                            {cp.course.estimatedHours ? (
                              <p className="text-micro text-muted-foreground">{cp.course.estimatedHours}h · {getProgramLabel(cp.course.difficultyLevel) || cp.course.difficultyLevel}</p>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {program.levels.length === 0 && (
            <div className="rounded-xl border border-border bg-card p-12 text-center">
              <p className="text-muted-foreground">Este programa aún no tiene niveles disponibles.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
